import React, { useContext, useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Input, Label } from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { pt } from "date-fns/locale";
import { format, parse } from "date-fns";
import BizinBackend from "../../../../helpers/bizin_helper";
import useGetCsrfToken from "../../../../Hooks/useGetCsrfToken";
import { DF_LO0021Context } from "../context";
import TranspaleStringToComponente from "../../../../components/Dynamic/TranspaleStringToComponente";
import {
  rotaFreteSchema,
  validarAoMenosUmComponente,
  derivarTipoCalculo,
  TEXTOS_APOIO_COMPONENTES,
} from "../constants";

const inputClassName = "rounded font-size-14 border-color-cinza2";
const moedaPlaceholder = "R$ 0,00";
const sqlToBr = (d) => {
  if (!d) return "";
  if (typeof d === "string" && d.includes("T")) {
    const parts = d.split("T")[0].split("-");
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  const dt = new Date(d);
  if (isNaN(dt)) return "";
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
};

function RotaFreteForm({ mode = "create", ID_Tabela_Frete = null, onClose, onSaved } = {}) {
  const ctx = useContext(DF_LO0021Context);
  const { access_token, userProfile: reduxUserProfile } = useSelector((state) => ({
    access_token: state.login.user?.access_token,
    userProfile: state.login.profile,
  }));
  const userProfile = ctx?.userProfile ?? reduxUserProfile;
  const navigate = ctx?.navigate;
  const csrfToken = useGetCsrfToken();

  const readOnly = mode === "view";

  const [formData, setFormData] = useState({
    CNPJ_Emissor: "",
    CNPJ_Remetente: "",
    CNPJ_Destinatario: "",
    ID_Tipo_Veiculo: "",
    Chave_Regiao_Origem: "",
    Chave_Local_Origem: "",
    Chave_Regiao_Destino: "",
    Chave_Local_Destino: "",
    Data_Inicio_Validade: "",
    Data_Termino_Validade: "",
    Frete_Fixo: "",
    Frete_Peso: "",
    Frete_Percentual: "",
    Frete_Cubagem: "",
    Frete_Perc_Servico: "",
    Fator_Cubagem: "",
    Frete_Minimo: "",
    Peso_Minimo: "",
    Peso_Maximo: "",
    Valor_NF_Minimo: "",
    Valor_NF_Maximo: "",
    Valor_Adicional_Fator: "",
  });

  const [errors, setErrors] = useState({});
  const [screenMode, setScreenMode] = useState("normal"); // normal | confirm | success | error | cancelConfirm
  const [resultMessage, setResultMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [rotaLabel, setRotaLabel] = useState(""); // ex: "SÃO PAULO → RIO LARGO"
  const [tipoCalculo, setTipoCalculo] = useState("—");

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: type === "checkbox" ? checked : value,
    }));
  };
  const extractVal = (v) => {
    if (!v) return null;
    if (typeof v === "string" || typeof v === "number") return v;
    if (typeof v === "object") {
      if (v.value !== undefined && (typeof v.value === "string" || typeof v.value === "number")) return v.value;
      if (v.value !== undefined && typeof v.value === "object") return extractVal(v.value);
    }
    return null;
  };
  const normalizeFormData = (data) => {
    const normalized = { ...data };
    const comboFields = [
      "CNPJ_Emissor", "CNPJ_Remetente", "CNPJ_Destinatario", "ID_Tipo_Veiculo",
      "Chave_Regiao_Origem", "Chave_Local_Origem", "Chave_Regiao_Destino", "Chave_Local_Destino",
    ];
    comboFields.forEach((field) => {
      const v = normalized[field];
      if (v && typeof v === "object") {
        const val = extractVal(v);
        const lbl = (typeof v.label === "string" && v.label) || (v.value && typeof v.value === "object" && v.value.label) || val;
        normalized[field] = val ? { value: val, label: lbl } : "";
      }
    });
    return normalized;
  };
  const handlePreFilter = (filters) => {
    setFormData((p) => normalizeFormData({ ...p, ...filters }));
  };
  useEffect(() => {
    setTipoCalculo(derivarTipoCalculo(formData));
  }, [formData]);
  const carregarRota = useCallback(async () => {
    if (!ID_Tabela_Frete) return;
    try {
      const resp = await BizinBackend.executeProcedure(
        { 1: ID_Tabela_Frete },
        "FRONT_Workspace_DF_LO0020_Obter_Rota",
        csrfToken,
        access_token
      );
      const dados = Array.isArray(resp) ? resp[0] : resp?.resultset_1?.[0];
      if (dados) {
        setFormData({
          CNPJ_Emissor: dados.ID_Empresa_Emissor
            ? { value: dados.ID_Empresa_Emissor, label: dados.Nome_Emissor || String(dados.ID_Empresa_Emissor) }
            : "",
          CNPJ_Remetente: dados.ID_Empresa_Remetente
            ? { value: dados.ID_Empresa_Remetente, label: dados.Nome_Remetente || String(dados.ID_Empresa_Remetente) }
            : "",
          CNPJ_Destinatario: dados.ID_Empresa_Destinatario
            ? { value: dados.ID_Empresa_Destinatario, label: dados.Nome_Destinatario || String(dados.ID_Empresa_Destinatario) }
            : "",
          ID_Tipo_Veiculo: dados.ID_Tipo_Veiculo
            ? { value: dados.ID_Tipo_Veiculo, label: dados.Nome_Veiculo || String(dados.ID_Tipo_Veiculo) }
            : "",
          Chave_Regiao_Origem: { value: dados.Chave_Regiao_Origem, label: dados.Chave_Regiao_Origem },
          Chave_Local_Origem: { value: dados.Chave_Local_Origem, label: dados.Chave_Local_Origem },
          Chave_Regiao_Destino: { value: dados.Chave_Regiao_Destino, label: dados.Chave_Regiao_Destino },
          Chave_Local_Destino: { value: dados.Chave_Local_Destino, label: dados.Chave_Local_Destino },
          Data_Inicio_Validade: sqlToBr(dados.Data_Inicio_Validade),
          Data_Termino_Validade: sqlToBr(dados.Data_Termino_Validade),
          Frete_Fixo: dados.Frete_Fixo ?? "",
          Frete_Peso: dados.Frete_Peso ?? "",
          Frete_Percentual: dados.Frete_Percentual ?? "",
          Frete_Cubagem: dados.Frete_Cubagem ?? "",
          Frete_Perc_Servico: dados.Frete_Perc_Servico ?? "",
          Fator_Cubagem: dados.Fator_Cubagem ?? "",
          Frete_Minimo: dados.Frete_Minimo ?? "",
          Peso_Minimo: dados.Peso_Minimo ?? "",
          Peso_Maximo: dados.Peso_Maximo ?? "",
          Valor_NF_Minimo: dados.Valor_NF_Minimo ?? "",
          Valor_NF_Maximo: dados.Valor_NF_Maximo ?? "",
          Valor_Adicional_Fator: dados.Valor_Adicional_Fator ?? "",
        });
        setRotaLabel(`${dados.Chave_Local_Origem || ""} → ${dados.Chave_Local_Destino || ""}`);
      }
    } catch (error) {
      console.error("Erro ao carregar rota:", error);
    }
  }, [ID_Tabela_Frete, csrfToken, access_token]);

  useEffect(() => {
    if ((mode === "edit" || mode === "view") && csrfToken && access_token) {
      carregarRota();
    }
  }, [mode, csrfToken, access_token, carregarRota]);
  const validarForm = async () => {
    try {
      await rotaFreteSchema.validate(formData, { abortEarly: false });
      const erroComponente = validarAoMenosUmComponente(formData);
      if (erroComponente) {
        setErrors({ __componente: erroComponente });
        return false;
      }
      setErrors({});
      return true;
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((e) => {
        newErrors[e.path] = e.message;
      });
      setErrors(newErrors);
      return false;
    }
  };

  const handleSave = async () => {
    const valido = await validarForm();
    if (!valido) {
      setResultMessage("Revise os campos obrigatórios antes de continuar.");
      setScreenMode("error");
      return;
    }
    setScreenMode("confirm");
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const isEdit = mode === "edit" && ID_Tabela_Frete;
      const dataInicio = formData.Data_Inicio_Validade
        ? new Date(formData.Data_Inicio_Validade.split("/").reverse().join("-")).toISOString()
        : null;
      const dataTermino = formData.Data_Termino_Validade
        ? new Date(formData.Data_Termino_Validade.split("/").reverse().join("-")).toISOString()
        : null;

      const num = (v) => (v ? Number(v) : 0);

      let params;
      if (isEdit) {
        params = {
          1: Number(ID_Tabela_Frete),                    // @ID_Tabela_Frete
          2: extractVal(formData.CNPJ_Emissor),          // @ID_Empresa_Emissor
          3: extractVal(formData.CNPJ_Remetente),        // @ID_Empresa_Remetente
          4: extractVal(formData.CNPJ_Destinatario),     // @ID_Empresa_Destinatario
          5: extractVal(formData.Chave_Local_Origem),    // @Chave_Local_Origem
          6: extractVal(formData.Chave_Regiao_Origem),    // @Chave_Regiao_Origem
          7: extractVal(formData.Chave_Local_Destino),    // @Chave_Local_Destino
          8: extractVal(formData.Chave_Regiao_Destino),   // @Chave_Regiao_Destino
          9: Number(extractVal(formData.ID_Tipo_Veiculo)),// @ID_Tipo_Veiculo
          10: dataInicio,                                // @Data_Inicio_Validade
          11: dataTermino,                                // @Data_Termino_Validade
          12: num(formData.Frete_Fixo),
          13: num(formData.Frete_Peso),
          14: num(formData.Frete_Percentual),
          15: num(formData.Frete_Cubagem),
          16: num(formData.Frete_Perc_Servico),
          17: num(formData.Fator_Cubagem),
          18: num(formData.Frete_Minimo),
          19: num(formData.Peso_Minimo),
          20: num(formData.Peso_Maximo),
          21: num(formData.Valor_NF_Minimo),
          22: num(formData.Valor_NF_Maximo),
          23: num(formData.Valor_Adicional_Fator),
          24: userProfile?.ID_Usuario,
        };
      } else {
        params = {
          1: extractVal(formData.CNPJ_Emissor),          // @ID_Empresa_Emissor
          2: extractVal(formData.CNPJ_Remetente),        // @ID_Empresa_Remetente
          3: extractVal(formData.CNPJ_Destinatario),     // @ID_Empresa_Destinatario
          4: extractVal(formData.Chave_Local_Origem),    // @Chave_Local_Origem
          5: extractVal(formData.Chave_Regiao_Origem),  // @Chave_Regiao_Origem
          6: extractVal(formData.Chave_Local_Destino),   // @Chave_Local_Destino
          7: extractVal(formData.Chave_Regiao_Destino),  // @Chave_Regiao_Destino
          8: Number(extractVal(formData.ID_Tipo_Veiculo)),// @ID_Tipo_Veiculo
          9: dataInicio,                                // @Data_Inicio_Validade
          10: dataTermino,                               // @Data_Termino_Validade
          11: num(formData.Frete_Fixo),
          12: num(formData.Frete_Peso),
          13: num(formData.Frete_Percentual),
          14: num(formData.Frete_Cubagem),
          15: num(formData.Frete_Perc_Servico),
          16: num(formData.Fator_Cubagem),
          17: num(formData.Frete_Minimo),
          18: num(formData.Peso_Minimo),
          19: num(formData.Peso_Maximo),
          20: num(formData.Valor_NF_Minimo),
          21: num(formData.Valor_NF_Maximo),
          22: num(formData.Valor_Adicional_Fator),
          23: userProfile?.ID_Usuario,
        };
      }
      Object.keys(params).forEach((k) => {
        if (typeof params[k] === "number" && isNaN(params[k])) params[k] = 0;
      });

      let procName;
      if (isEdit) {
        procName = "FRONT_Workspace_DF_LO0020_Atualizar_Rota";
      } else {
        procName = "FRONT_Workspace_DF_LO0020_Inserir_Rota";
      }

      const resp = await BizinBackend.executeProcedure(
        params,
        procName,
        csrfToken,
        access_token
      );

      const result = Array.isArray(resp) ? resp[0] : resp?.resultset_1?.[0];
      if (result?.Status === "OK") {
        setScreenMode("success");
        setResultMessage(
          mode === "edit"
            ? `A rota ${rotaLabel} foi atualizada com sucesso.`
            : `A sua rota ${rotaLabel} foi criada com sucesso.`
        );
      } else {
        setScreenMode("error");
        setResultMessage(result?.Mensagem || "Erro ao salvar rota.");
      }
    } catch (error) {
      console.error("Erro ao salvar:", error);
      setScreenMode("error");
      setResultMessage("Erro ao salvar rota. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onClose) return onClose();
    if (window.parent !== window) {
      window.parent.postMessage({ type: "closeModal", isReloadPage: false }, "*");
    } else {
      navigate("/DF_LO0020");
    }
  };

  const handleSuccessClose = () => {
    if (onSaved) return onSaved();
    if (window.parent !== window) {
      window.parent.postMessage({ type: "closeModal", isReloadPage: true }, "*");
    } else {
      navigate("/DF_LO0020");
    }
  };

  const handleCancel = () => {
    setScreenMode("cancelConfirm");
  };

  const handleCancelConfirm = () => {
    if (onClose) return onClose();
    if (window.parent !== window) {
      window.parent.postMessage({ type: "closeModal", isReloadPage: false }, "*");
    } else {
      navigate("/DF_LO0020");
    }
  };
  const renderCampoComponente = (campo, label, isPercent = false) => (
    <div className="rota-field">
      <Label for={campo} className="text-color-cinza4">
        {label}
      </Label>
      <Input
        type="number"
        id={campo}
        value={formData[campo]}
        onChange={handleInputChange}
        disabled={readOnly}
        className={inputClassName}
        placeholder={isPercent ? "0.00" : moedaPlaceholder}
        min="0"
        step="0.01"
      />
      {TEXTOS_APOIO_COMPONENTES[campo] && (
        <small className="text-muted font-size-12">{TEXTOS_APOIO_COMPONENTES[campo]}</small>
      )}
      {errors[campo] && <div className="text-danger font-size-12">{errors[campo]}</div>}
    </div>
  );

  if (screenMode === "confirm") {
    return (
      <div className="rota-confirm-container">
        <div className="icon-warning">
          <i className="fas fa-exclamation-triangle"></i>
        </div>
        <h5 className="modal-title-custom">
          {mode === "edit" ? "Deseja salvar as alterações da rota?" : "Deseja criar a rota?"}
        </h5>
        <p className="modal-subtext">
          {mode === "edit" ? "Ao clicar em Confirmar as alterações serão salvas." : "Ao clicar em Confirmar a rota será criada."}
        </p>
        <div className="modal-actions">
          <button className="btn fw-semibold btn-cancelar-co0005" onClick={() => setScreenMode("normal")} disabled={loading}>
            Cancelar
          </button>
          <button className="btn fw-semibold btn-confirmar-co0005" onClick={handleConfirm} disabled={loading}>
            {loading ? "Aguardando..." : "Confirmar"}
          </button>
        </div>
      </div>
    );
  }

  if (screenMode === "success") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center text-center" style={{ padding: "40px 24px" }}>
        <div className="icon-success">
          <i className="fas fa-check"></i>
        </div>
        <h5 className="modal-title-custom">Sucesso!</h5>
        <p className="modal-subtext">{resultMessage}</p>
        <button className="btn fw-semibold btn-confirmar-co0005" onClick={handleSuccessClose}>
          Ok!
        </button>
      </div>
    );
  }

  if (screenMode === "error") {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center text-center" style={{ padding: "40px 24px" }}>
        <div className="icon-error">
          <i className="fas fa-times"></i>
        </div>
        <h5 className="modal-title-custom mb-3">Ops!</h5>
        <p className="modal-subtext">{resultMessage}</p>
        <button className="btn fw-semibold btn-confirmar-co0005" onClick={() => setScreenMode("normal")}>
          Ok!
        </button>
      </div>
    );
  }

  if (screenMode === "cancelConfirm") {
    return (
      <div className="rota-confirm-container">
        <div className="icon-warning">
          <i className="fas fa-exclamation-triangle"></i>
        </div>
        <h5 className="modal-title-custom">
          {mode === "edit" ? "Deseja cancelar a edição da rota?" : "Deseja cancelar a criação da rota?"}
        </h5>
        <p className="modal-subtext">O progresso será perdido.</p>
        <div className="modal-actions">
          <button className="btn fw-semibold btn-confirmar-co0005" onClick={() => setScreenMode("normal")}>
            Voltar
          </button>
          <button className="btn fw-semibold btn-cancelar-co0005" onClick={handleCancelConfirm}>
            Ok!
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rota-form-container">
      {/* ===== Seção: Identificação ===== */}
      <div className="rota-form-section">
        <h6 className="rota-section-title">Identificação</h6>
        <div className="rota-form-grid rota-grid-2col">
          <div className="rota-field">
            <Label className="text-color-cinza4">Transportadora <span className="text-danger">*</span></Label>
            <TranspaleStringToComponente
              elementId="CNPJ_Emissor"
              elementProps={{
                value: formData.CNPJ_Emissor,
                type: "Combobox",
                title: "",
                id: "CNPJ_Emissor",
                isDisabled: readOnly,
                procedure: "FRONT_Componente_Combo_Empresas",
                procedureParams: [userProfile?.ID_Usuario, "pt-BR", "ORIGEM", "CT"],
              }}
              preFilter={[formData]}
              onPreFilter={handlePreFilter}
            />
            {errors.CNPJ_Emissor && <div className="text-danger font-size-12">{errors.CNPJ_Emissor}</div>}
          </div>
          <div className="rota-field">
            <Label className="text-color-cinza4">Fornecedor</Label>
            <TranspaleStringToComponente
              elementId="CNPJ_Remetente"
              elementProps={{
                value: formData.CNPJ_Remetente,
                type: "Combobox",
                title: "",
                id: "CNPJ_Remetente",
                isDisabled: readOnly,
                procedure: "FRONT_Componente_Combo_Empresas",
                procedureParams: [userProfile?.ID_Usuario, "pt-BR", "ORIGEM", "NF"],
              }}
              preFilter={[formData]}
              onPreFilter={handlePreFilter}
            />
            <small className="rota-field-hint text-muted font-size-12">Opcional - Deixe em branco para aplicar a todos</small>
          </div>
          <div className="rota-field">
            <Label className="text-color-cinza4">Local de entrega <span className="text-danger">*</span></Label>
            <TranspaleStringToComponente
              elementId="CNPJ_Destinatario"
              elementProps={{
                value: formData.CNPJ_Destinatario,
                type: "Combobox",
                title: "",
                id: "CNPJ_Destinatario",
                isDisabled: readOnly,
                procedure: "FRONT_Componente_Combo_Empresas",
                procedureParams: [userProfile?.ID_Usuario, "pt-BR", "DESTINO", "NF"],
              }}
              preFilter={[formData]}
              onPreFilter={handlePreFilter}
            />
            {errors.CNPJ_Destinatario && <div className="text-danger font-size-12">{errors.CNPJ_Destinatario}</div>}
          </div>
          <div className="rota-field">
            <Label className="text-color-cinza4">Veículo <span className="text-danger">*</span></Label>
            <TranspaleStringToComponente
              elementId="ID_Tipo_Veiculo"
              elementProps={{
                value: formData.ID_Tipo_Veiculo,
                type: "Combobox",
                title: "",
                id: "ID_Tipo_Veiculo",
                isDisabled: readOnly,
                procedure: "FRONT_Conhecimento_Transporte_Veiculo_Obter_Lista",
                procedureParams: readOnly ? [null, null] : [null, "<#CNPJ_Destinatario#>"],
                alienated: readOnly ? [] : ["CNPJ_Destinatario"],
              }}
              preFilter={[formData]}
              onPreFilter={handlePreFilter}
            />
            {errors.ID_Tipo_Veiculo && <div className="text-danger font-size-12">{errors.ID_Tipo_Veiculo}</div>}
          </div>
        </div>
      </div>

      {/* ===== Seção: Rota ===== */}
      <div className="rota-form-section">
        <h6 className="rota-section-title">Rota</h6>
        <div className="rota-form-grid rota-grid-uf-municipio">
          <div className="rota-field rota-field-uf">
            <Label className="text-color-cinza4">UF de origem <span className="text-danger">*</span></Label>
            <TranspaleStringToComponente
              elementId="Chave_Regiao_Origem"
              elementProps={{
                value: formData.Chave_Regiao_Origem,
                type: "Combobox",
                title: "",
                id: "Chave_Regiao_Origem",
                isDisabled: readOnly,
                procedure: "FRONT_Componente_Combo_UF_Origem",
                procedureParams: [],
              }}
              preFilter={[formData]}
              onPreFilter={handlePreFilter}
            />
            {errors.Chave_Regiao_Origem && <div className="text-danger font-size-12">{errors.Chave_Regiao_Origem}</div>}
          </div>
          <div className="rota-field rota-field-municipio">
            <Label className="text-color-cinza4">Município de origem <span className="text-danger">*</span></Label>
            <TranspaleStringToComponente
              elementId="Chave_Local_Origem"
              elementProps={{
                value: formData.Chave_Local_Origem,
                type: "Combobox",
                title: "",
                id: "Chave_Local_Origem",
                isDisabled: readOnly,
                procedure: "FRONT_Componente_Combo_Municipios_Origem",
                procedureParams: readOnly ? [] : ["<#Chave_Regiao_Origem#>"],
                alienated: readOnly ? [] : ["Chave_Regiao_Origem"],
              }}
              preFilter={[formData]}
              onPreFilter={handlePreFilter}
            />
            {errors.Chave_Local_Origem && <div className="text-danger font-size-12">{errors.Chave_Local_Origem}</div>}
          </div>
          <div className="rota-field rota-field-uf">
            <Label className="text-color-cinza4">UF de destino <span className="text-danger">*</span></Label>
            <TranspaleStringToComponente
              elementId="Chave_Regiao_Destino"
              elementProps={{
                value: formData.Chave_Regiao_Destino,
                type: "Combobox",
                title: "",
                id: "Chave_Regiao_Destino",
                isDisabled: readOnly,
                procedure: "FRONT_Componente_Combo_UF_Destino",
                procedureParams: [],
              }}
              preFilter={[formData]}
              onPreFilter={handlePreFilter}
            />
            {errors.Chave_Regiao_Destino && <div className="text-danger font-size-12">{errors.Chave_Regiao_Destino}</div>}
          </div>
          <div className="rota-field rota-field-municipio">
            <Label className="text-color-cinza4">Município de destino <span className="text-danger">*</span></Label>
            <TranspaleStringToComponente
              elementId="Chave_Local_Destino"
              elementProps={{
                value: formData.Chave_Local_Destino,
                type: "Combobox",
                title: "",
                id: "Chave_Local_Destino",
                isDisabled: readOnly,
                procedure: "FRONT_Componente_Combo_Municipios_Destino",
                procedureParams: readOnly ? [] : ["<#Chave_Regiao_Destino#>"],
                alienated: readOnly ? [] : ["Chave_Regiao_Destino"],
              }}
              preFilter={[formData]}
              onPreFilter={handlePreFilter}
            />
            {errors.Chave_Local_Destino && <div className="text-danger font-size-12">{errors.Chave_Local_Destino}</div>}
          </div>
        </div>
      </div>

      {/* ===== Seção: Vigência da Regra ===== */}
      <div className="rota-form-section">
        <h6 className="rota-section-title">Vigência da Regra</h6>
        <div className="rota-form-grid rota-grid-2col">
          <div className="rota-field">
            <Label className="text-color-cinza4">Data Inicial</Label>
            <div className="rota-date-input-group">
              <DatePicker
                selected={formData.Data_Inicio_Validade ? parse(formData.Data_Inicio_Validade, "dd/MM/yyyy", new Date()) : null}
                onChange={(date) => setFormData((p) => ({ ...p, Data_Inicio_Validade: date ? format(date, "dd/MM/yyyy") : "" }))}
                dateFormat="dd/MM/yyyy"
                locale={pt}
                disabled={readOnly}
                placeholderText="dd/mm/aaaa"
                className="rota-date-input"
                wrapperClassName="rota-date-wrapper"
              />
              <div className="rota-date-addon">
                <i className="fa-solid fa-calendar-days" style={{ color: "#263973", fontSize: "16px" }}></i>
              </div>
            </div>
            {errors.Data_Inicio_Validade && <div className="text-danger font-size-12">{errors.Data_Inicio_Validade}</div>}
          </div>
          <div className="rota-field">
            <Label className="text-color-cinza4">Data Final</Label>
            <div className="rota-date-input-group">
              <DatePicker
                selected={formData.Data_Termino_Validade ? parse(formData.Data_Termino_Validade, "dd/MM/yyyy", new Date()) : null}
                onChange={(date) => setFormData((p) => ({ ...p, Data_Termino_Validade: date ? format(date, "dd/MM/yyyy") : "" }))}
                dateFormat="dd/MM/yyyy"
                locale={pt}
                disabled={readOnly}
                placeholderText="dd/mm/aaaa"
                className="rota-date-input"
                wrapperClassName="rota-date-wrapper"
              />
              <div className="rota-date-addon">
                <i className="fa-solid fa-calendar-days" style={{ color: "#263973", fontSize: "16px" }}></i>
              </div>
            </div>
            {errors.Data_Termino_Validade && <div className="text-danger font-size-12">{errors.Data_Termino_Validade}</div>}
          </div>
        </div>
      </div>

      {/* ===== Seção: Componentes do Frete ===== */}
      <div className="rota-form-section">
        <h6 className="rota-section-title">Componentes do Frete</h6>
        <div className="mb-2 font-size-13 text-muted">
          Tipo de cálculo derivado: <strong className="text-color-azul1">{tipoCalculo}</strong>
        </div>
        <div className="rota-form-grid rota-grid-3col">
          {renderCampoComponente("Frete_Fixo", "Frete Fixo (R$)")}
          {renderCampoComponente("Frete_Peso", "Frete Peso (R$/kg)")}
          {renderCampoComponente("Frete_Percentual", "Frete % NF", true)}
          {renderCampoComponente("Frete_Cubagem", "Frete Cubagem (R$/m³)")}
          {renderCampoComponente("Frete_Perc_Servico", "Frete % Serviço", true)}
          {renderCampoComponente("Fator_Cubagem", "Fator Cubagem")}
        </div>
      </div>
      {errors.__componente && (
        <div className="alert alert-warning font-size-13">{errors.__componente}</div>
      )}

      {/* ===== Seção: Limites e Mínimos ===== */}
      <div className="rota-form-section">
        <h6 className="rota-section-title">Limites e Mínimos</h6>
        <div className="rota-form-grid rota-grid-3col">
          <div className="rota-field">
            <Label className="text-color-cinza4">Frete Mínimo (R$)</Label>
            <Input type="number" id="Frete_Minimo" value={formData.Frete_Minimo} onChange={handleInputChange} disabled={readOnly} className={inputClassName} placeholder={moedaPlaceholder} min="0" step="0.01" />
            {errors.Frete_Minimo && <div className="text-danger font-size-12">{errors.Frete_Minimo}</div>}
          </div>
          <div className="rota-field">
            <Label className="text-color-cinza4">Peso Mínimo (kg)</Label>
            <Input type="number" id="Peso_Minimo" value={formData.Peso_Minimo} onChange={handleInputChange} disabled={readOnly} className={inputClassName} placeholder="0,00" min="0" step="0.001" />
            {errors.Peso_Minimo && <div className="text-danger font-size-12">{errors.Peso_Minimo}</div>}
          </div>
          <div className="rota-field">
            <Label className="text-color-cinza4">Peso Máximo (kg)</Label>
            <Input type="number" id="Peso_Maximo" value={formData.Peso_Maximo} onChange={handleInputChange} disabled={readOnly} className={inputClassName} placeholder="0,00" min="0" step="0.001" />
            {errors.Peso_Maximo && <div className="text-danger font-size-12">{errors.Peso_Maximo}</div>}
          </div>
          <div className="rota-field">
            <Label className="text-color-cinza4">Valor NF Mínimo (R$)</Label>
            <Input type="number" id="Valor_NF_Minimo" value={formData.Valor_NF_Minimo} onChange={handleInputChange} disabled={readOnly} className={inputClassName} placeholder={moedaPlaceholder} min="0" step="0.01" />
            {errors.Valor_NF_Minimo && <div className="text-danger font-size-12">{errors.Valor_NF_Minimo}</div>}
          </div>
          <div className="rota-field">
            <Label className="text-color-cinza4">Valor NF Máximo (R$)</Label>
            <Input type="number" id="Valor_NF_Maximo" value={formData.Valor_NF_Maximo} onChange={handleInputChange} disabled={readOnly} className={inputClassName} placeholder={moedaPlaceholder} min="0" step="0.01" />
            {errors.Valor_NF_Maximo && <div className="text-danger font-size-12">{errors.Valor_NF_Maximo}</div>}
          </div>
          <div className="rota-field">
            <Label className="text-color-cinza4">Valor Adic. Fator (R$)</Label>
            <Input type="number" id="Valor_Adicional_Fator" value={formData.Valor_Adicional_Fator} onChange={handleInputChange} disabled={readOnly} className={inputClassName} placeholder={moedaPlaceholder} min="0" step="0.01" />
            {errors.Valor_Adicional_Fator && <div className="text-danger font-size-12">{errors.Valor_Adicional_Fator}</div>}
          </div>
        </div>
      </div>

      {/* ===== Footer / Ações ===== */}
      {!readOnly && (
        <div className="rota-form-actions">
          <button className="btn fw-semibold btn-cancelar-co0005" onClick={handleCancel}>
            Cancelar
          </button>
          <button className="btn fw-semibold btn-confirmar-co0005" onClick={handleSave}>
            {mode === "edit" ? "Salvar alterações" : "Criar rota"}
          </button>
        </div>
      )}

      {readOnly && (
        <div className="rota-form-actions">
          <button className="btn fw-semibold btn-cancelar-co0005" onClick={handleBack}>
            Voltar
          </button>
        </div>
      )}
    </div>
  );
}

export default RotaFreteForm;